/**
 * Presentation-only pure helpers for the tagging HUD.
 *
 * Same split as `round.ts` beside it: what the HUD *says* is decided here and
 * tested without a browser, and `tag-hud.ts` only puts it on screen.
 *
 * Everything here is cosmetic, and that is a stronger statement than it sounds.
 * The server is the only thing that decides whether a shot is allowed or
 * whether it hit — the client cannot evaluate either, since a hunter's snapshot
 * names disguised hiders as prop instances and never as players. So nothing in
 * this file may ever become an input to a decision; it exists so the player can
 * see what already happened.
 */

import {
  RoundPhase,
  TAG_COOLDOWN_TICKS,
  TAG_MISS_PENALTY_TICKS,
  TagOutcome,
  Team,
  TICK_RATE,
  type RoundPhaseId,
  type TeamId,
} from "@prop-hunt/shared";

/**
 * What the crosshair should look like.
 *
 * `Hidden` rather than "always show a dot": a hider has no weapon, and a
 * crosshair on their screen would suggest otherwise. It is also the honest
 * signal during Hiding, when even a hunter cannot shoot.
 */
export const CrosshairState = {
  Hidden: "hidden",
  Ready: "ready",
  Cooling: "cooling",
} as const;

export type CrosshairStateId = (typeof CrosshairState)[keyof typeof CrosshairState];

export function crosshairState(
  phase: RoundPhaseId,
  team: TeamId,
  found: boolean,
  cooldownTicksLeft: number,
): CrosshairStateId {
  if (found) return CrosshairState.Hidden;
  if (team !== Team.Hunter) return CrosshairState.Hidden;
  if (phase !== RoundPhase.Hunting) return CrosshairState.Hidden;
  return cooldownTicksLeft > 0 ? CrosshairState.Cooling : CrosshairState.Ready;
}

/**
 * How full the cooldown ring is, 0 (just fired) to 1 (ready).
 *
 * Scaled against the penalty this outcome actually incurred rather than a
 * single constant, so a miss reads as the long wait it is instead of filling
 * at the same rate as the short wait after a hit.
 */
export function cooldownProgress(ticksLeft: number, totalTicks: number): number {
  if (totalTicks <= 0) return 1;
  const done = 1 - ticksLeft / totalTicks;
  return done < 0 ? 0 : done > 1 ? 1 : done;
}

/**
 * One line about a shot, or `null` for one nobody needs to be told about.
 *
 * Written from the recipient's point of view rather than the shooter's, which
 * is the whole reason this takes `localId`: "you found Sam", "Sam found you"
 * and "someone found someone" are three different messages about one event, and
 * only the last is what a bystander should get.
 */
export function tagMessage(
  tag: { shooterId: number; outcome: number; targetId: number },
  localId: number,
  nameOf: (id: number) => string,
): string | null {
  const mine = tag.shooterId === localId;

  if (tag.outcome === TagOutcome.Cooldown) {
    // Only ever sent to the shooter, and only to explain a press that did
    // nothing. A bystander seeing this would be reading someone else's mail.
    return mine ? "still reloading" : null;
  }

  if (tag.outcome === TagOutcome.Missed) {
    // A miss by someone else is a gunshot: worth hearing, not worth narrating.
    return mine ? "missed" : null;
  }

  if (tag.targetId === localId) return `FOUND by ${nameOf(tag.shooterId)}`;
  if (mine) return `found ${nameOf(tag.targetId)}`;
  return `${nameOf(tag.shooterId)} found ${nameOf(tag.targetId)}`;
}

/**
 * Ticks of cooldown an outcome starts, for the client's own display.
 *
 * Deliberately re-derived from the shared constants rather than sent: the
 * server owns the real deadline and refuses an early shot regardless, so a
 * number on the wire would be a second authority for something already decided.
 *
 * The client's copy runs slightly *long* — it starts when the Msg.Tag arrives,
 * which is after the server began counting. That direction is the safe one: the
 * player is never shown a ready crosshair that the server would refuse.
 */
export function cooldownTotalFor(outcome: number): number {
  if (outcome === TagOutcome.Found) return TAG_COOLDOWN_TICKS;
  if (outcome === TagOutcome.Missed) return TAG_MISS_PENALTY_TICKS;
  return 0;
}

/** Seconds left on a cooldown, for a readout. Rounded up so 0.1s reads as 1. */
export function cooldownSeconds(ticksLeft: number): number {
  return ticksLeft <= 0 ? 0 : Math.ceil(ticksLeft / TICK_RATE);
}
